import React, { useState } from 'react';
import { useSubscriptionsStore } from '../../stores/useSubscriptionsStore';
import { MemberSubscription } from '../../stores/useSubscriptionsStore';
import { XMarkIcon } from '@heroicons/react/24/outline';

/** Cliente may be object or array (Supabase join). Supports nombre_completo OR nombre + apellido. */
function getCustomerName(cliente: unknown): string {
  if (!cliente) return '—';
  const c = Array.isArray(cliente) ? cliente[0] : cliente;
  const o = c as { nombre?: string; apellido?: string; nombre_completo?: string };
  return (o?.nombre_completo ?? `${o?.nombre || ''} ${o?.apellido || ''}`.trim()) || '—';
}

function getProductName(sub: MemberSubscription): string {
  const p = sub.producto;
  if (!p) return '—';
  const prod = Array.isArray(p) ? p[0] : p;
  return (prod as { nombre?: string })?.nombre ?? '—';
}

function getProductPrecio(sub: MemberSubscription): number {
  const p = sub.producto;
  if (!p) return 0;
  const prod = Array.isArray(p) ? p[0] : p;
  return Number((prod as { precio?: number })?.precio) || 0;
}

interface RecordPaymentModalProps {
  subscription: MemberSubscription;
  onClose: () => void;
  onSuccess: () => void;
}

export function RecordPaymentModal({ subscription, onClose, onSuccess }: RecordPaymentModalProps) {
  const { recordPayment } = useSubscriptionsStore();
  const [amount, setAmount] = useState(getProductPrecio(subscription) || 0);
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const daysOverdue = subscription.days_overdue ?? 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await recordPayment(subscription.id, amount, paymentMethod);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al registrar pago');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" aria-hidden onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Registrar pago</h2>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="font-medium text-gray-900">{getCustomerName(subscription.cliente)}</p>
              <p className="text-sm text-gray-600">{getProductName(subscription)}</p>
              {daysOverdue > 0 && (
                <p className="text-sm text-red-600 mt-1">⚠️ {daysOverdue} días de retraso</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monto</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Método de pago</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="efectivo">Efectivo</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="transferencia">Transferencia</option>
                <option value="nequi">Nequi</option>
                <option value="daviplata">Daviplata</option>
                <option value="link_pago">Link de pago</option>
              </select>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                {isSubmitting ? 'Guardando...' : 'Registrar pago'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
